import { initializeApp } from "firebase/app";
import { initializeFirestore } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  indexedDBLocalPersistence,
  initializeAuth
} from "firebase/auth";
import { getStorage } from "firebase/storage";
import { getAnalytics, isSupported, type Analytics } from "firebase/analytics";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};
const app = initializeApp(firebaseConfig);
export const functions = getFunctions(app, "asia-northeast1");
if (
  import.meta.env.DEV &&
  import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === "true"
) {
  connectFunctionsEmulator(functions, "localhost", 5001);
}
// iOS（iPhone/iPad の全ブラウザ＝WebKit）と macOS Safari では、Firestore の WebChannel
// ストリーミング接続が ITP / access-control で弾かれて「offline」化し、getRole などの読み取りが
// 数十秒スタックする（実機 iPhone Safari の HAR/console で確認済み: リロード時に Firestore 依存
// 処理だけが進まず "情報取得中" で停止）。これらの WebKit 系では long-polling を「強制」して
// WebChannel を試行せず安定接続する。auto-detect は WebChannel を先に試して弾かれてからの
// フォールバックが iPhone Safari では不安定なため、検出に頼らず force する。
// WebChannel が正常に使える PC の Chrome / Edge / Firefox（Blink/Gecko）は従来どおり
// auto-detect（=通常 WebChannel、デグレなし）。
const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
const isIOS =
  /iPad|iPhone|iPod/.test(ua) ||
  // iPadOS 13+ は MacIntel + タッチとして報告される
  (typeof navigator !== "undefined" &&
    navigator.platform === "MacIntel" &&
    (navigator.maxTouchPoints ?? 0) > 1);
// macOS Safari 判定。Chrome/Edge/Opera/Firefox 等の WebKit 非系を誤検出しないよう、
// Apple ベンダー＋Safari UA かつ他ブラウザ識別子を除外する（iOS は上の isIOS で別途 force）。
const isDesktopSafari =
  typeof navigator !== "undefined" &&
  navigator.vendor === "Apple Computer, Inc." &&
  /safari/i.test(ua) &&
  !/chrome|crios|chromium|edg|edgios|opr|opera|fxios|android/i.test(ua);
// ChromeOS（Chromebook）は Blink で WebChannel 自体は扱えるが、学校で使われる Chromebook は
// プロキシ / コンテンツフィルタ配下が多く、フィルタが WebChannel の長時間ストリーミングを切って
// 同様の offline スタックを起こす。auto-detect のフォールバックは遅いため、ChromeOS も
// long-polling を強制する（良好回線では僅かに非効率だが、学校環境のプロキシ耐性を優先）。
const isChromeOS = /CrOS/.test(ua);
const shouldForceLongPolling = isIOS || isDesktopSafari || isChromeOS;
export const db = initializeFirestore(
  app,
  shouldForceLongPolling
    ? { experimentalForceLongPolling: true }
    : { experimentalAutoDetectLongPolling: true }
);
const canUseLocalStorage = (() => {
  if (typeof window === "undefined") return false;

  const testKey = "school-ai:auth-storage-check";
  try {
    window.localStorage.setItem(testKey, "1");
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
})();

// Firebase Auth の既定 persistence は IndexedDB を最優先し、長時間開いたタブで
// Safari の IDB 接続が切れると SDK 内部の polling が unhandled rejection になる。
// このアプリは popup/redirect 認証を使わず、独自 OAuth / custom token で認証するため、
// 通常の保存先は localStorage（利用不可時は sessionStorage）にする。IndexedDB は
// localStorage が利用できる場合にだけ、既存ユーザーを移す migration source として末尾に
// 加える。移行後は localStorage だけが監視される。localStorage 自体が禁止された環境では
// IDB を候補から外し、sessionStorage（利用不可ならメモリ）に限定して polling を防ぐ。
const authPersistence = canUseLocalStorage
  ? [
      browserLocalPersistence,
      browserSessionPersistence,
      indexedDBLocalPersistence
    ]
  : [browserSessionPersistence];

export const auth = initializeAuth(app, {
  persistence: authPersistence
});
export const storage = getStorage(app);

// Analytics は browser extension / cookie禁止 / IndexedDB禁止などでは利用できない。
// getAnalytics() を先に呼ぶと、対応外環境でも Installations の IndexedDB 初期化が走り、
// SecurityError や AbortError が未処理Promiseとして送信されるため、公式の互換性判定を
// 通過したブラウザでだけ初期化する。
const analyticsPromise: Promise<Analytics | null> =
  typeof navigator !== "undefined" && navigator.onLine === false
    ? Promise.resolve(null)
    : isSupported()
        .then(supported => (supported ? getAnalytics(app) : null))
        .catch(error => {
          console.warn(
            "Firebase Analytics is unavailable in this browser",
            error
          );
          return null;
        });

export const getAnalyticsIfSupported = (): Promise<Analytics | null> =>
  analyticsPromise;
